import { useState } from 'react';
import { supabase } from '../supabaseClient';

const W = { bg:'#0A1526', surface:'#16233F', border:'#28395C', muted:'#7d8ea2', bright:'#f1f5f9', accent:'#00A0C8', danger:'#f87171' };
const inp = { width:'100%', padding:'9px 12px', borderRadius:'7px', border:`1px solid ${W.border}`, background:W.bg, color:W.bright, fontSize:'13px', boxSizing:'border-box' };
const lbl = { fontSize:'11px', fontWeight:'600', color:W.muted, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:'6px' };
const g2  = { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'12px' };

function Toggle({ val, set }) {
  return (
    <button onClick={() => set(v => !v)}
      style={{ width:'36px', height:'20px', borderRadius:'10px', border:'none', cursor:'pointer', flexShrink:0,
        background: val ? W.accent : W.border, position:'relative', transition:'background 0.2s' }}>
      <div style={{ position:'absolute', top:'2px', left: val ? '18px' : '2px', width:'16px', height:'16px',
        borderRadius:'50%', background:'#fff', transition:'left 0.2s' }} />
    </button>
  );
}

export default function NewClientWizard({ org, onSave, onClose }) {
  const STEPS = ['Billing Model', 'Rates', 'Contact', 'Address & Options'];
  const [step, setStep]     = useState(0);
  const [saving, setSaving] = useState(false);
  const [err, setErr]       = useState('');

  const [name, setName]         = useState('');
  const [model, setModel]       = useState('');
  const [isInternal, setIsInternal] = useState(false);

  const [rate, setRate]         = useState('');
  const [retainer, setRetainer] = useState('');
  const [hours, setHours]       = useState('');
  const [overage, setOverage]   = useState('');
  const [threshold, setThreshold] = useState('');

  const [contactName, setContactName]   = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [reminders, setReminders]       = useState(true);

  const [addrLine1, setAddrLine1] = useState('');
  const [addrLine2, setAddrLine2] = useState('');
  const [city, setCity]           = useState('');
  const [addrState, setAddrState] = useState('');
  const [zip, setZip]             = useState('');
  const [country, setCountry]     = useState('');
  const [isNonprofit, setIsNonprofit] = useState(false);
  const [donationPct, setDonationPct] = useState('');

  const canNext = () => step === 0 ? name.trim().length > 0 && model !== '' : true;
  const next = () => { setErr(''); if (canNext()) setStep(s => s + 1); else setErr('Please fill in the required fields above.'); };
  const back = () => { setErr(''); setStep(s => s - 1); };

  const save = async () => {
    if (!name.trim() || !model) { setErr('Client name and billing model are required.'); return; }
    setSaving(true);
    const { error } = await supabase.from('clients').insert({
      org_id: org.id, name: name.trim(), model,
      rate: parseFloat(rate) || 0, retainer: parseFloat(retainer) || 0,
      hours: parseFloat(hours) || 0, overage: parseFloat(overage) || 0,
      monthly_hours_threshold: threshold ? parseFloat(threshold) : null,
      is_internal: isInternal, is_nonprofit: isNonprofit,
      donation_percentage: isNonprofit && donationPct ? parseFloat(donationPct) : null,
      billing_contact_name: contactName || null, billing_contact_email: contactEmail || null,
      invoice_reminders_enabled: reminders,
      address_line1: addrLine1 || null, address_line2: addrLine2 || null,
      city: city || null, state: addrState || null, zip: zip || null, country: country || null,
      is_active: true,
    });
    setSaving(false);
    if (error) { setErr(error.message); return; }
    onSave();
    onClose();
  };

  const MODEL_CARDS = [
    { id:'hourly',   label:'Hourly',          icon:'⏱', desc:'Bill by the hour. Each logged entry × your rate. Best for variable-scope work.' },
    { id:'retainer', label:'Retainer',        icon:'📅', desc:'Flat monthly fee regardless of hours. Best for ongoing fixed-scope engagements.' },
    { id:'hybrid',   label:'Retainer + overage', icon:'⚖️', desc:'Flat fee covers included hours; hours beyond that bill at an overage rate.' },
  ];

  const stepContent = [
    <div key={0}>
      <div style={{ marginBottom:'20px' }}>
        <div style={lbl}>Client Name <span style={{ color:W.danger }}>*</span></div>
        <input style={inp} placeholder="Acme Corp" value={name} onChange={e => setName(e.target.value)} autoFocus />
      </div>
      <div style={{ marginBottom:'20px' }}>
        <div style={lbl}>Billing Model <span style={{ color:W.danger }}>*</span></div>
        <div style={{ display:'flex', flexDirection:'column', gap:'8px' }}>
          {MODEL_CARDS.map(m => (
            <div key={m.id} onClick={() => setModel(m.id)}
              style={{ display:'flex', alignItems:'flex-start', gap:'12px', padding:'12px 14px', borderRadius:'8px', cursor:'pointer',
                border:`2px solid ${model === m.id ? W.accent : W.border}`,
                background: model === m.id ? 'rgba(0,160,200,0.07)' : W.bg, transition:'all 0.15s' }}>
              <div style={{ fontSize:'20px', marginTop:'1px' }}>{m.icon}</div>
              <div>
                <div style={{ fontSize:'13px', fontWeight:'700', color:W.bright, marginBottom:'3px' }}>{m.label}</div>
                <div style={{ fontSize:'12px', color:W.muted, lineHeight:'1.4' }}>{m.desc}</div>
              </div>
              <div style={{ marginLeft:'auto', flexShrink:0 }}>
                <div style={{ width:'18px', height:'18px', borderRadius:'50%',
                  border:`2px solid ${model === m.id ? W.accent : W.border}`,
                  background: model === m.id ? W.accent : 'transparent',
                  display:'flex', alignItems:'center', justifyContent:'center' }}>
                  {model === m.id && <div style={{ width:'8px', height:'8px', borderRadius:'50%', background:'#fff' }} />}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', borderRadius:'8px', border:`1px solid ${W.border}`, background:W.bg }}>
        <div>
          <div style={{ fontSize:'13px', fontWeight:'600', color:W.bright }}>Internal client</div>
          <div style={{ fontSize:'11px', color:W.muted, marginTop:'2px' }}>Hides billing fields; excluded from invoices and revenue reporting.</div>
        </div>
        <Toggle val={isInternal} set={setIsInternal} />
      </div>
    </div>,

    <div key={1}>
      {model === 'hourly' && (
        <div style={{ marginBottom:'16px' }}>
          <div style={lbl}>Hourly Rate ($/hr)</div>
          <input style={{ ...inp, width:'160px' }} type="number" min="0" step="0.01" placeholder="e.g. 150" value={rate} onChange={e => setRate(e.target.value)} autoFocus />
          <div style={{ fontSize:'11px', color:W.muted, marginTop:'6px' }}>Amount billed per logged hour.</div>
        </div>
      )}
      {model === 'retainer' && (
        <div style={{ marginBottom:'16px' }}>
          <div style={lbl}>Monthly Fee ($)</div>
          <input style={{ ...inp, width:'160px' }} type="number" min="0" step="0.01" placeholder="e.g. 3000" value={retainer} onChange={e => setRetainer(e.target.value)} autoFocus />
          <div style={{ fontSize:'11px', color:W.muted, marginTop:'6px' }}>Flat amount billed each month regardless of hours.</div>
        </div>
      )}
      {model === 'hybrid' && (
        <div style={g2}>
          <div>
            <div style={lbl}>Monthly Base ($)</div>
            <input style={inp} type="number" min="0" step="0.01" placeholder="e.g. 2500" value={retainer} onChange={e => setRetainer(e.target.value)} autoFocus />
          </div>
          <div>
            <div style={lbl}>Included Hours (hrs/mo)</div>
            <input style={inp} type="number" min="0" step="0.5" placeholder="e.g. 20" value={hours} onChange={e => setHours(e.target.value)} />
          </div>
          <div>
            <div style={lbl}>Overage Rate ($/hr)</div>
            <input style={inp} type="number" min="0" step="0.01" placeholder="e.g. 150" value={overage} onChange={e => setOverage(e.target.value)} />
          </div>
        </div>
      )}
      <div style={{ marginTop:'20px', paddingTop:'16px', borderTop:`1px solid ${W.border}` }}>
        <div style={lbl}>Hours Threshold Alert <span style={{ fontWeight:'400', textTransform:'none', letterSpacing:0 }}>(optional)</span></div>
        <input style={{ ...inp, width:'160px' }} type="number" min="0" step="0.5" placeholder="e.g. 25" value={threshold} onChange={e => setThreshold(e.target.value)} />
        <div style={{ fontSize:'11px', color:W.muted, marginTop:'6px' }}>Send an alert email when monthly hours exceed this number. Leave blank to disable.</div>
      </div>
    </div>,

    <div key={2}>
      <div style={{ ...g2, marginBottom:'16px' }}>
        <div>
          <div style={lbl}>Billing Contact Name</div>
          <input style={inp} placeholder="Jane Smith" value={contactName} onChange={e => setContactName(e.target.value)} autoFocus />
        </div>
        <div>
          <div style={lbl}>Billing Contact Email</div>
          <input style={inp} type="email" placeholder="billing@client.com" value={contactEmail} onChange={e => setContactEmail(e.target.value)} />
        </div>
      </div>
      <div style={{ fontSize:'11px', color:W.muted, marginBottom:'20px' }}>Name and email shown on invoices and used for reminder delivery. You can add these later.</div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', borderRadius:'8px', border:`1px solid ${W.border}`, background:W.bg }}>
        <div>
          <div style={{ fontSize:'13px', fontWeight:'600', color:W.bright }}>Invoice reminder emails</div>
          <div style={{ fontSize:'11px', color:W.muted, marginTop:'2px' }}>When on, automated follow-ups are sent for unpaid invoices per your org schedule.</div>
        </div>
        <Toggle val={reminders} set={setReminders} />
      </div>
    </div>,

    <div key={3}>
      <div style={{ marginBottom:'12px' }}>
        <div style={lbl}>Address Line 1</div>
        <input style={inp} placeholder="123 Main St" value={addrLine1} onChange={e => setAddrLine1(e.target.value)} autoFocus />
      </div>
      <div style={{ marginBottom:'12px' }}>
        <div style={lbl}>Address Line 2 <span style={{ fontWeight:'400', textTransform:'none', letterSpacing:0 }}>(Suite, Unit, etc.)</span></div>
        <input style={inp} placeholder="Suite 100" value={addrLine2} onChange={e => setAddrLine2(e.target.value)} />
      </div>
      <div style={{ ...g2, marginBottom:'12px' }}>
        <div><div style={lbl}>City</div><input style={inp} placeholder="New York" value={city} onChange={e => setCity(e.target.value)} /></div>
        <div><div style={lbl}>State</div><input style={inp} placeholder="NY" value={addrState} onChange={e => setAddrState(e.target.value)} /></div>
      </div>
      <div style={{ ...g2, marginBottom:'20px' }}>
        <div><div style={lbl}>ZIP / Postal Code</div><input style={inp} placeholder="10001" value={zip} onChange={e => setZip(e.target.value)} /></div>
        <div><div style={lbl}>Country</div><input style={inp} placeholder="United States" value={country} onChange={e => setCountry(e.target.value)} /></div>
      </div>
      <div style={{ borderTop:`1px solid ${W.border}`, paddingTop:'16px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 14px', borderRadius:'8px', border:`1px solid ${W.border}`, background:W.bg, marginBottom:'10px' }}>
          <div>
            <div style={{ fontSize:'13px', fontWeight:'600', color:W.bright }}>Nonprofit client</div>
            <div style={{ fontSize:'11px', color:W.muted, marginTop:'2px' }}>Would you like to plan a donation? Enable to track an optional giving amount per invoice.</div>
          </div>
          <Toggle val={isNonprofit} set={setIsNonprofit} />
        </div>
        {isNonprofit && (
          <div style={{ marginTop:'8px', padding:'0 2px' }}>
            <div style={lbl}>Donation Percentage (%)</div>
            <input style={{ ...inp, width:'140px' }} type="number" min="0" max="100" step="0.1" placeholder="e.g. 5" value={donationPct} onChange={e => setDonationPct(e.target.value)} />
            <div style={{ fontSize:'11px', color:W.muted, marginTop:'6px' }}>This percentage will be used to calculate a suggested donation amount from each invoice total.</div>
          </div>
        )}
      </div>
    </div>,
  ];

  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.75)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1200, padding:'20px' }}>
      <div style={{ background:W.surface, border:`1px solid ${W.border}`, borderRadius:'14px', width:'100%', maxWidth:'520px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 20px 60px rgba(0,0,0,0.6)' }}>
        <div style={{ padding:'22px 24px 16px', borderBottom:`1px solid ${W.border}`, flexShrink:0 }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <div>
              <div style={{ fontSize:'17px', fontWeight:'800', color:W.bright }}>New Client</div>
              <div style={{ fontSize:'12px', color:W.muted, marginTop:'3px' }}>Step {step + 1} of {STEPS.length} — {STEPS[step]}</div>
            </div>
            <button onClick={onClose} style={{ background:'transparent', border:'none', color:W.muted, fontSize:'22px', cursor:'pointer', lineHeight:1, padding:'0 0 0 12px' }}>×</button>
          </div>
          <div style={{ marginTop:'14px', height:'3px', borderRadius:'2px', background:W.border }}>
            <div style={{ height:'100%', borderRadius:'2px', background:W.accent, width:`${((step + 1) / STEPS.length) * 100}%`, transition:'width 0.3s' }} />
          </div>
        </div>
        <div style={{ padding:'22px 24px', overflowY:'auto', flex:1 }}>
          {stepContent[step]}
          {err && <div style={{ marginTop:'12px', fontSize:'12px', color:W.danger }}>{err}</div>}
        </div>
        <div style={{ padding:'16px 24px', borderTop:`1px solid ${W.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexShrink:0 }}>
          <button onClick={step === 0 ? onClose : back}
            style={{ padding:'8px 18px', borderRadius:'7px', border:`1px solid ${W.border}`, background:'transparent', color:W.muted, fontSize:'13px', cursor:'pointer' }}>
            {step === 0 ? 'Cancel' : '← Back'}
          </button>
          {step < STEPS.length - 1
            ? <button onClick={next} disabled={!canNext()}
                style={{ padding:'8px 22px', borderRadius:'7px', border:'none', background: canNext() ? W.accent : W.border, color: canNext() ? '#fff' : W.muted, fontSize:'13px', fontWeight:'600', cursor: canNext() ? 'pointer' : 'default', transition:'all 0.15s' }}>
                Continue →
              </button>
            : <button onClick={save} disabled={saving}
                style={{ padding:'8px 22px', borderRadius:'7px', border:'none', background: saving ? W.border : W.accent, color: saving ? W.muted : '#fff', fontSize:'13px', fontWeight:'600', cursor: saving ? 'default' : 'pointer' }}>
                {saving ? 'Saving…' : 'Save Client'}
              </button>
          }
        </div>
      </div>
    </div>
  );
}
